'use client'

import { HTMLProps, useRef, useState } from 'react'
import clsx from 'classnames'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useLocale } from 'use-intl'
import Image from 'next/image'
import CrossIcon from '@/public/cross.svg'
import { FiltersBar } from '@/shared/ui/filters-bar'
import { AnalyticsAsyncList } from '@/features/analytics-list'
import { Pagination } from '@/shared/ui/pagination'
import { DatePicker } from '@/shared/ui/date-picker'
import type { IPostPreview, IPostCategory } from '@/interfaces/analytics'
import getPosts, { GetPostsParams } from '@/actions/getPosts'
import { Value } from '@/shared/ui/date-picker/date-picker'
import { getCategoryId, getDateRange } from '@/shared/utils'
import { localeMap } from '@/shared/utils/directus'
import { useRouter } from '@/routing'
import { IPerson } from '@/interfaces/persons'
import style from './analytics-feed.module.scss'

type AnalyticsFeedProps = HTMLProps<HTMLDivElement> & {
  categories: IPostCategory[]
  author?: IPerson | null
  initData: { items: IPostPreview[]; page: number; limit: number; total: string | number }
}

export const AnalyticsFeed = (props: AnalyticsFeedProps) => {
  const { className, initData, categories, author: initAuthor } = props
  const { items, page, total, limit } = initData
  const currentLocale = useLocale()
  const [currentPage, setCurrentPage] = useState(page)
  const [pageSize, setPageSize] = useState(limit)
  const [totalCount, setTotalCount] = useState(total as number)
  const [author, setAuthor] = useState(initAuthor)
  const filterRef = useRef<IPostCategory | null>(null)
  const dateRef = useRef<Value>(null)
  const [pages, setPages] = useState<Record<number, IPostPreview[]>>({ 0: items })
  const router = useRouter()

  const totalPageCount = Math.ceil(totalCount / pageSize)
  const parentRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: totalPageCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 900,
    overscan: 1,
  })
  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  const fetchData = ({ categoryId, dateRange, page }: GetPostsParams) => {
    getPosts({ locale: localeMap[currentLocale], categoryId, page, dateRange })
      .then(({ items, total, limit }) => {
        setPageSize(limit)
        setTotalCount(total as number)
        if (page) {
          setPages(prevState => ({ ...prevState, [page - 1]: items }))
        } else {
          setPages({ 0: items })
          setCurrentPage(1)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const onChangeCategory = (filter: IPostCategory | null) => {
    const categoryId = getCategoryId(filter)
    const dateRange = getDateRange(dateRef.current)

    filterRef.current = filter

    fetchData({ categoryId, dateRange })
  }

  const onFetchPage = (page: number) => {
    const categoryId = getCategoryId(filterRef.current)
    const dateRange = getDateRange(dateRef.current)

    fetchData({ page, dateRange, categoryId })
  }

  const onDateChange = (newDateRange: Value) => {
    const categoryId = getCategoryId(filterRef.current)
    const dateRange = getDateRange(newDateRange)
    dateRef.current = newDateRange

    fetchData({ categoryId, dateRange })
  }

  return (
    <section
      className={clsx(style.container, 'main-padding', 'white-section white-section-js', className)}
      ref={parentRef}
    >
      {author ? (
        <div className={clsx(style.author, 'text text_type_t2')}>
          {author.image && (
            <div className={style.avatar}>
              <Image src={author.image?.url} alt={author.name} fill style={{ objectFit: 'cover' }} />
            </div>
          )}
          {author.name}
          <CrossIcon
            onClick={() => {
              onFetchPage(1)
              setAuthor(undefined)
              router.replace('/analytics')
            }}
          />
        </div>
      ) : (
        <FiltersBar
          activeFilter={filterRef.current}
          filters={categories}
          onFilterChange={onChangeCategory}
          renderButton={restProps => (
            <DatePicker initDate={dateRef.current} onDateChange={onDateChange} {...restProps} />
          )}
        />
      )}

      {virtualItems?.length ? (
        <ul
          style={{
            height: totalSize,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map(virtualRow => (
            <li
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow?.start ?? 0}px)`,
              }}
            >
              <AnalyticsAsyncList
                anchorId={`page${virtualRow.index}`}
                cards={pages[virtualRow.index]}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                onCurrentPage={setCurrentPage}
                onFetchPage={onFetchPage}
              />
            </li>
          ))}
        </ul>
      ) : (
        <AnalyticsAsyncList
          key="initAnalyticsAsyncList"
          anchorId={`page0`}
          cards={pages[0]}
          data-index={0}
          onCurrentPage={setCurrentPage}
        />
      )}

      <Pagination
        className={style.pagination}
        currentPage={currentPage}
        onPageChange={page => {
          location.href = `#page${page - 1}`
          setCurrentPage(page)
        }}
        pageSize={pageSize}
        totalPageCount={totalPageCount}
      />
    </section>
  )
}
